import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton } from '@mui/material';
import {
  ColumnHeaderFilterIconButtonProps,
  DataGridProps,
  gridClasses,
  GridColDef,
  GridFilterInputValue,
  GridFilterOperator,
  useGridApiContext,
} from '@mui/x-data-grid';
import React from 'react';
import { formatDuration } from './shared-cells';

// Shared DataGrid behaviour for the Manager Tab grids.
//  - Sorting: click the column header (the arrow in the header).
//  - Filtering: a magnifier in each column header opens the filter panel for
//    that column, above the header so the panel never covers rows. The panel
//    has one value box that does a "contains" match, so its Columns and
//    Operator pickers are hidden.
//  - There is no 3-dot column menu.

// The field of the column whose magnifier was clicked last. The filter panel
// opens above that header (the grid's default is under the header, on top of
// the first rows). Only one panel is open at a time, so one field name is enough.
// Storing the field name instead of a DOM reference avoids holding a detached
// node when the DataGrid remounts after a tab switch.
const filterPanelAnchor: { field: string | null } = { field: null };

/**
 * Magnifier in the column header: one click opens the filter panel for that
 * column. It takes the place of the grid's own "this column is filtered"
 * indicator (same slot), so it is also what marks a filtered column.
 */
function HeaderFilterButton({
  field,
  counter,
}: ColumnHeaderFilterIconButtonProps) {
  const apiRef = useGridApiContext();
  if (!apiRef.current.getColumn(field).filterable) {
    return null; // the checkbox column and action columns (Release / Edit)
  }
  const label = apiRef.current.getLocaleText('columnMenuFilter') as string;
  return (
    // Same class as the sort arrow's container, so the grid's own CSS applies:
    // hidden until the header is hovered, always shown while the column is
    // filtered (or sorted), and always shown on touch screens.
    <Box
      className={gridClasses.iconButtonContainer}
      sx={{ display: 'flex', visibility: 'hidden', width: 0 }}
    >
      <IconButton
        size="small"
        tabIndex={-1}
        aria-label={label}
        title={label}
        color={counter ? 'primary' : 'default'}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation(); // a click on the header would also sort the column
          filterPanelAnchor.field = field;
          apiRef.current.showFilterPanel(field);
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const HIDDEN_INPUT = { sx: { display: 'none' } };

/**
 * Spread onto each Manager Tab `<DataGrid>`.
 *
 * The MIT DataGrid only ever holds one filter, and the header button creates it
 * for its own column. The column is therefore already known and the operator is
 * always "contains" (see containsFilterColumnProps for numbers), so the panel
 * only needs the value box.
 */
export const managerTabGridProps: Pick<
  DataGridProps,
  | 'disableColumnMenu'
  | 'disableVirtualization'
  | 'filterDebounceMs'
  | 'slots'
  | 'slotProps'
> = {
  disableColumnMenu: true,
  // jsdom has no layout width, so virtualization would skip cells/headers in tests.
  disableVirtualization: Boolean(process.env.JEST_WORKER_ID),
  filterDebounceMs: process.env.JEST_WORKER_ID ? 0 : 150,
  slots: { columnHeaderFilterIconButton: HeaderFilterButton },
  slotProps: {
    panel: {
      placement: 'top-start',
      anchorEl: () => {
        const f = filterPanelAnchor.field;
        return (
          (f
            ? document.querySelector<HTMLElement>(
                `[role="columnheader"][data-field="${f}"]`,
              )
            : null) ?? document.body
        );
      },
      popperOptions: {
        modifiers: [
          // A small gap between the panel and the header.
          { name: 'offset', options: { offset: [0, 4] } },
          // The grid only flips the panel when it would leave the whole
          // document. Flip when it would leave the visible window instead, so
          // a header scrolled to the top of the screen does not push the
          // panel out of sight (it then opens below the header).
          { name: 'flip', options: { rootBoundary: 'viewport' } },
        ],
      },
    },
    filterPanel: {
      filterFormProps: {
        columnInputProps: HIDDEN_INPUT,
        operatorInputProps: HIDDEN_INPUT,
      },
    },
  },
};

/**
 * Column props that make the filter of a `type: 'number'` column a
 * case-insensitive "contains" on `toText(value)` (default: the number's digits).
 *
 * Text columns already get "contains" from the grid. Number columns get =, >, <
 * and so on, and with the operator picker hidden the user could not reach
 * "contains" on them. `valueParser` is overridden too: a number column runs the
 * typed filter text through Number() before the operator sees it, which would
 * turn "30m" into NaN.
 */
export function containsFilterColumnProps(
  toText: (value: unknown) => string = String,
): {
  valueParser: GridColDef['valueParser'];
  filterOperators: GridFilterOperator[];
} {
  return {
    valueParser: value => value,
    filterOperators: [
      {
        value: 'contains',
        getApplyFilterFn: filterItem => {
          const needle = String(filterItem.value ?? '')
            .trim()
            .toLowerCase();
          if (!needle) {
            return null; // empty box = no filtering
          }
          return value =>
            value != null && toText(value).toLowerCase().includes(needle);
        },
        InputComponent: GridFilterInputValue,
      },
    ],
  };
}

/** "contains" against the duration text shown in the cell, e.g. "2h 30m". */
export const durationFilterColumnProps = containsFilterColumnProps(hours =>
  formatDuration(Number(hours)),
);
